import { A } from '@adornis/base/env-info.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { authenticatedMiddleware } from '../permissions/middlewares/authenticatedMiddleware.js';
import { Person } from '../users/entities/person.js';
import { UserBasedViewProfile } from './subscribeUserBasedViewProfiles.js';

/**
 * Passes one of the caller's own views on to a colleague as a copy. A copy rather than a shared view: the
 * recipient can adjust or delete it without touching the original, and nobody needs edit rights on another
 * person's view. The name carries the sender, so the recipient knows where it came from.
 */
export const resolveCopyUserBasedViewProfileToUser = (viewID: string, recipientID: string) => async () => {
  const views = await getRawCollection<EntityData<UserBasedViewProfile>>(UserBasedViewProfile._collectionName);
  // the sender is whoever owns the view, so a server context has nobody to copy from
  const ownerID = context.serverContext ? null : context.userID;
  const view = ownerID ? await views.findOne({ _id: viewID, userId: ownerID }) : null;
  if (!view) throw new ExpectedError('Nur eigene, gespeicherte Ansichten können weitergegeben werden.');

  const persons = await getRawCollection<EntityData<Person>>(Person._collectionName);
  if (!(await persons.findOne({ _id: recipientID }, { projection: { _id: 1 } })))
    throw new ExpectedError('Die ausgewählte Person existiert nicht.');

  const sender = await getMyself<Person>()({ profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } });
  const senderName = [sender?.profilDaten?.stammdaten?.vorname, sender?.profilDaten?.stammdaten?.nachname]
    .filter(Boolean)
    .join(' ');

  const copy = {
    ...view,
    _id: A.getGloballyUniqueID(),
    userId: recipientID,
    name: senderName ? `${view.name} (von ${senderName})` : view.name,
  };
  await views.insertOne(copy);
  return copy._id;
};

export const copyUserBasedViewProfileToUser = registerMutation({
  operationName: 'copyUserBasedViewProfileToUser',
  middlewares: [authenticatedMiddleware],
  type: () => String,
  params: [
    { type: () => String, name: 'viewID' },
    { type: () => String, name: 'recipientID' },
  ],
  resolve: resolveCopyUserBasedViewProfileToUser,
});
